import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import {
  Building2,
  FileKey2,
  FileQuestion,
  FileText,
  Globe2,
  HelpCircle,
  Loader2,
  LockKeyhole,
  Save,
  Settings as SettingsIcon,
  ShieldCheck,
  UserRound,
  Video,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";

function SectionCard({ icon: Icon, title, description, children }) {
  return (
    <section className="rsi-card p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-9 w-9 rounded-lg bg-[var(--rsi-purple-tint)] grid place-items-center text-[var(--rsi-purple)]">
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <h2 className="font-display font-extrabold text-lg tracking-tight text-[var(--rsi-navy)]">
            {title}
          </h2>
          {description ? <p className="text-sm text-gray-500 mt-0.5">{description}</p> : null}
        </div>
      </div>
      {children}
    </section>
  );
}

function CmsHint({ children }) {
  return <p className="mt-1 text-xs text-gray-500">{children}</p>;
}

export default function AdminSettings() {
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["admin-settings"],
    queryFn: async () => (await api.get("/admin/settings")).data,
  });

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const saveMut = useMutation({
    mutationFn: async (payload) => (await api.put("/admin/settings", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-settings"] });
      qc.invalidateQueries({ queryKey: ["public-settings"] });
      toast.success("Settings saved");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const passwordMut = useMutation({
    mutationFn: async (payload) => (await api.post("/admin/account/change-password", payload)).data,
    onSuccess: (result) => {
      setPasswordForm({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
      toast.success(result?.message || "Admin password updated successfully");
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const controlSummary = useMemo(
    () => [
      {
        label: "Public pages synced",
        value: "Home, Products, Contact, Trust, FAQ, Terms, Privacy",
      },
      {
        label: "Brand & legal CMS",
        value: "Hero, company, footer, social links, legal copy",
      },
      {
        label: "Admin security",
        value: "Password can be changed directly from this panel",
      },
    ],
    []
  );

  if (isLoading || !form) {
    return (
      <div className="text-center py-20"><Loader2 className="h-6 w-6 animate-spin inline text-[var(--rsi-purple)]" /></div>
    );
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setPassword = (k) => (e) => setPasswordForm((prev) => ({ ...prev, [k]: e.target.value }));

  const save = (e) => {
    e.preventDefault();
    saveMut.mutate(form);
  };

  const changePassword = (e) => {
    e.preventDefault();
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error("Enter the current password and a new password");
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("New password confirmation does not match");
      return;
    }
    passwordMut.mutate({
      current_password: passwordForm.current_password,
      new_password: passwordForm.new_password,
    });
  };

  return (
    <div data-testid="admin-settings" className="max-w-5xl">
      <div className="mb-8">
        <span className="badge-purple">
          <SettingsIcon className="h-3.5 w-3.5" /> Site Configuration
        </span>
        <h1 className="font-display text-4xl font-black tracking-tighter mt-3 text-[var(--rsi-navy)]">
          Settings
        </h1>
        <p className="text-gray-600 text-sm mt-1">
          Manage the synced content hub for the main website. Everything saved here is used by the public pages.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3 mb-8">
        {controlSummary.map((item) => (
          <div key={item.label} className="rsi-card p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--rsi-purple)]">{item.label}</p>
            <p className="mt-2 text-sm leading-6 text-gray-700">{item.value}</p>
          </div>
        ))}
      </div>

      <form onSubmit={save} className="grid gap-8">
        <SectionCard
          icon={Video}
          title="Hero section"
          description="Controls the homepage hero video, poster image, headline, and first call-to-action."
        >
          <div className="grid gap-4">
            <div>
              <Label>Hero video URL (mp4, looping)</Label>
              <Input
                value={form.hero_video_url || ""}
                onChange={set("hero_video_url")}
                placeholder="https://…/hero.mp4"
                className="mt-1.5 font-mono-plex"
                data-testid="settings-video-url"
              />
              <p className="text-xs text-gray-500 mt-1">Leave blank to use the default stock video. Paste a direct .mp4 URL.</p>
            </div>
            <div>
              <ImageUpload
                label="Hero poster image (used as video fallback)"
                value={form.hero_poster_url}
                onChange={(url) => setForm({ ...form, hero_poster_url: url })}
                testId="hero-poster-upload"
              />
            </div>
            <div>
              <Label>Hero Headline</Label>
              <Input value={form.hero_headline || ""} onChange={set("hero_headline")} className="mt-1.5" data-testid="settings-headline" />
            </div>
            <div>
              <Label>Hero Sub-headline</Label>
              <Textarea
                rows={3}
                value={form.hero_subheadline || ""}
                onChange={set("hero_subheadline")}
                className="mt-1.5"
                data-testid="settings-subheadline"
              />
            </div>
            <div>
              <Label>Hero CTA Label</Label>
              <Input value={form.hero_cta_label || ""} onChange={set("hero_cta_label")} className="mt-1.5" />
            </div>
          </div>
        </SectionCard>

        <SectionCard
          icon={Building2}
          title="Company information"
          description="Shared company identity used across the website, footer, and contact areas."
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Company name</Label>
              <Input value={form.company_name || ""} onChange={set("company_name")} className="mt-1.5" />
            </div>
            <div>
              <Label>Tagline</Label>
              <Input value={form.tagline || ""} onChange={set("tagline")} className="mt-1.5" />
            </div>
            <div>
              <Label>Proprietor</Label>
              <Input value={form.proprietor || ""} onChange={set("proprietor")} className="mt-1.5" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={form.email || ""} onChange={set("email")} className="mt-1.5" />
            </div>
            <div>
              <Label>Primary Phone</Label>
              <Input value={form.phone_primary || ""} onChange={set("phone_primary")} className="mt-1.5" />
            </div>
            <div>
              <Label>Secondary Phone</Label>
              <Input value={form.phone_secondary || ""} onChange={set("phone_secondary")} className="mt-1.5" />
            </div>
          </div>
          <div className="mt-4">
            <Label>Address</Label>
            <Input value={form.address || ""} onChange={set("address")} className="mt-1.5" />
          </div>
          <div className="mt-4">
            <Label>About — Short</Label>
            <Textarea rows={3} value={form.about_short || ""} onChange={set("about_short")} className="mt-1.5" />
          </div>
        </SectionCard>

        <SectionCard
          icon={Globe2}
          title="About page sync"
          description="Founder history, company story, and organization profile messaging."
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Founder Name</Label>
              <Input value={form.founder_name || ""} onChange={set("founder_name")} className="mt-1.5" />
            </div>
            <div>
              <Label>Company Since</Label>
              <Input value={form.company_since || ""} onChange={set("company_since")} className="mt-1.5" />
            </div>
            <div className="sm:col-span-2">
              <Label>Company Location</Label>
              <Input value={form.company_location || ""} onChange={set("company_location")} className="mt-1.5" />
            </div>
            <div>
              <Label>About Story Heading</Label>
              <Input value={form.about_story_heading || ""} onChange={set("about_story_heading")} className="mt-1.5" />
            </div>
            <div>
              <Label>About Story Highlight</Label>
              <Input value={form.about_story_highlight || ""} onChange={set("about_story_highlight")} className="mt-1.5" />
            </div>
            <div className="sm:col-span-2">
              <Label>About Story Paragraph 1</Label>
              <Textarea rows={4} value={form.about_story_p1 || ""} onChange={set("about_story_p1")} className="mt-1.5" />
            </div>
            <div className="sm:col-span-2">
              <Label>About Story Paragraph 2</Label>
              <Textarea rows={4} value={form.about_story_p2 || ""} onChange={set("about_story_p2")} className="mt-1.5" />
            </div>
          </div>
        </SectionCard>

        <SectionCard
          icon={UserRound}
          title="CEO profile sync"
          description="Profile card image, title, intro, and quote for the leadership areas."
        >
          <div className="grid gap-4">
            <ImageUpload
              label="CEO profile photo"
              value={form.ceo_photo_url}
              onChange={(url) => setForm({ ...form, ceo_photo_url: url })}
              testId="ceo-photo-upload"
            />
            <div>
              <Label>CEO Title</Label>
              <Input value={form.ceo_title || ""} onChange={set("ceo_title")} className="mt-1.5" />
            </div>
            <div>
              <Label>CEO Intro</Label>
              <Textarea rows={5} value={form.ceo_intro || ""} onChange={set("ceo_intro")} className="mt-1.5" />
            </div>
            <div>
              <Label>CEO Quote</Label>
              <Textarea rows={3} value={form.ceo_quote || ""} onChange={set("ceo_quote")} className="mt-1.5" />
            </div>
          </div>
        </SectionCard>

        <SectionCard
          icon={Building2}
          title="About page CMS"
          description="Manage the public About page hero, stats, values, and founder visual content."
        >
          <div className="grid gap-4">
            <ImageUpload
              label="Founder profile photo"
              value={form.founder_photo_url}
              onChange={(url) => setForm({ ...form, founder_photo_url: url })}
              testId="founder-photo-upload"
            />
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>About Badge</Label>
                <Input value={form.about_page_badge || ""} onChange={set("about_page_badge")} className="mt-1.5" />
              </div>
              <div>
                <Label>About Title</Label>
                <Input value={form.about_page_title || ""} onChange={set("about_page_title")} className="mt-1.5" />
              </div>
              <div className="sm:col-span-2">
                <Label>About Highlight</Label>
                <Input value={form.about_page_highlight || ""} onChange={set("about_page_highlight")} className="mt-1.5" />
              </div>
              <div className="sm:col-span-2">
                <Label>About Hero Image URL</Label>
                <Input value={form.about_page_hero_image_url || ""} onChange={set("about_page_hero_image_url")} className="mt-1.5 font-mono-plex" />
              </div>
            </div>
            <div className="grid gap-4">
              <div>
                <Label>About Intro Paragraph 1</Label>
                <Textarea rows={3} value={form.about_page_intro_1 || ""} onChange={set("about_page_intro_1")} className="mt-1.5" />
              </div>
              <div>
                <Label>About Intro Paragraph 2</Label>
                <Textarea rows={3} value={form.about_page_intro_2 || ""} onChange={set("about_page_intro_2")} className="mt-1.5" />
              </div>
              <div>
                <Label>About Intro Paragraph 3</Label>
                <Textarea rows={3} value={form.about_page_intro_3 || ""} onChange={set("about_page_intro_3")} className="mt-1.5" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Primary CTA Label</Label>
                <Input value={form.about_page_primary_cta || ""} onChange={set("about_page_primary_cta")} className="mt-1.5" />
              </div>
              <div>
                <Label>Secondary CTA Label</Label>
                <Input value={form.about_page_secondary_cta || ""} onChange={set("about_page_secondary_cta")} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label>About Stats</Label>
              <Textarea rows={6} value={form.about_page_stats || ""} onChange={set("about_page_stats")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use one stat per line in the format: Value|Label</CmsHint>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Values Badge</Label>
                <Input value={form.about_page_values_badge || ""} onChange={set("about_page_values_badge")} className="mt-1.5" />
              </div>
              <div>
                <Label>Values Title</Label>
                <Input value={form.about_page_values_title || ""} onChange={set("about_page_values_title")} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label>Values Items</Label>
              <Textarea rows={8} value={form.about_page_values_items || ""} onChange={set("about_page_values_items")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use one value per line in the format: Title|Body</CmsHint>
            </div>
          </div>
        </SectionCard>

        <SectionCard
          icon={Building2}
          title="Founder profile CMS"
          description="Control the founder profile page, life story, facts, and early business history."
        >
          <div className="grid gap-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Founder Badge</Label>
                <Input value={form.founder_profile_badge || ""} onChange={set("founder_profile_badge")} className="mt-1.5" />
              </div>
              <div>
                <Label>Founder Role Title</Label>
                <Input value={form.founder_profile_title || ""} onChange={set("founder_profile_title")} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label>Founder Intro</Label>
              <Textarea rows={4} value={form.founder_profile_intro || ""} onChange={set("founder_profile_intro")} className="mt-1.5" />
            </div>
            <div>
              <Label>Founder Highlights</Label>
              <Textarea rows={6} value={form.founder_profile_highlights || ""} onChange={set("founder_profile_highlights")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use one highlight per line.</CmsHint>
            </div>
            <div>
              <Label>Life Story Title</Label>
              <Input value={form.founder_profile_story_title || ""} onChange={set("founder_profile_story_title")} className="mt-1.5" />
            </div>
            <div>
              <Label>Life Story Paragraphs</Label>
              <Textarea rows={8} value={form.founder_profile_story_paragraphs || ""} onChange={set("founder_profile_story_paragraphs")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use one paragraph per line.</CmsHint>
            </div>
            <div>
              <Label>Legacy Title</Label>
              <Input value={form.founder_profile_legacy_title || ""} onChange={set("founder_profile_legacy_title")} className="mt-1.5" />
            </div>
            <div>
              <Label>Legacy Paragraphs</Label>
              <Textarea rows={7} value={form.founder_profile_legacy_paragraphs || ""} onChange={set("founder_profile_legacy_paragraphs")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use one paragraph per line.</CmsHint>
            </div>
            <div>
              <Label>Timeline Items</Label>
              <Textarea rows={10} value={form.founder_profile_timeline_items || ""} onChange={set("founder_profile_timeline_items")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use one timeline item per line in the format: Title|Body</CmsHint>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Birth</Label>
                <Input value={form.founder_profile_fact_birth || ""} onChange={set("founder_profile_fact_birth")} className="mt-1.5" />
              </div>
              <div>
                <Label>Passing</Label>
                <Input value={form.founder_profile_fact_passing || ""} onChange={set("founder_profile_fact_passing")} className="mt-1.5" />
              </div>
              <div>
                <Label>Office Phone</Label>
                <Input value={form.founder_profile_fact_office_phone || ""} onChange={set("founder_profile_fact_office_phone")} className="mt-1.5" />
              </div>
              <div>
                <Label>Profession Label</Label>
                <Input value={form.founder_profile_profession_label || ""} onChange={set("founder_profile_profession_label")} className="mt-1.5" />
              </div>
              <div className="sm:col-span-2">
                <Label>Office Address</Label>
                <Input value={form.founder_profile_office_address || ""} onChange={set("founder_profile_office_address")} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label>Early Business Focus</Label>
              <Textarea rows={6} value={form.founder_profile_focus_items || ""} onChange={set("founder_profile_focus_items")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use one focus item per line.</CmsHint>
            </div>
          </div>
        </SectionCard>

        <SectionCard
          icon={UserRound}
          title="CEO profile CMS"
          description="Control the CEO profile page, leadership sections, experience, education, languages, and awards."
        >
          <div className="grid gap-4">
            <div>
              <Label>CEO Profile Badge</Label>
              <Input value={form.ceo_profile_badge || ""} onChange={set("ceo_profile_badge")} className="mt-1.5" />
            </div>
            <div>
              <Label>CEO Highlights</Label>
              <Textarea rows={5} value={form.ceo_profile_highlights || ""} onChange={set("ceo_profile_highlights")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use one highlight per line.</CmsHint>
            </div>
            <div>
              <Label>Vision Title</Label>
              <Input value={form.ceo_profile_vision_title || ""} onChange={set("ceo_profile_vision_title")} className="mt-1.5" />
            </div>
            <div>
              <Label>Vision Paragraphs</Label>
              <Textarea rows={6} value={form.ceo_profile_vision_paragraphs || ""} onChange={set("ceo_profile_vision_paragraphs")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use one paragraph per line.</CmsHint>
            </div>
            <div>
              <Label>Vision Points</Label>
              <Textarea rows={6} value={form.ceo_profile_vision_points || ""} onChange={set("ceo_profile_vision_points")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use one point per line.</CmsHint>
            </div>
            <div>
              <Label>Leadership Title</Label>
              <Input value={form.ceo_profile_leadership_title || ""} onChange={set("ceo_profile_leadership_title")} className="mt-1.5" />
            </div>
            <div>
              <Label>Leadership Paragraphs</Label>
              <Textarea rows={6} value={form.ceo_profile_leadership_paragraphs || ""} onChange={set("ceo_profile_leadership_paragraphs")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use one paragraph per line.</CmsHint>
            </div>
            <div>
              <Label>Leadership Milestones</Label>
              <Textarea rows={7} value={form.ceo_profile_milestones || ""} onChange={set("ceo_profile_milestones")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use one milestone per line.</CmsHint>
            </div>
            <div>
              <Label>Experience Items</Label>
              <Textarea rows={12} value={form.ceo_profile_experience_items || ""} onChange={set("ceo_profile_experience_items")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use one role per line in the format: Period|Location|Company|Role|Point 1~~Point 2~~Point 3</CmsHint>
            </div>
            <div>
              <Label>Languages</Label>
              <Textarea rows={6} value={form.ceo_profile_languages || ""} onChange={set("ceo_profile_languages")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use one language per line in the format: Name|Level</CmsHint>
            </div>
            <div>
              <Label>Education Items</Label>
              <Textarea rows={8} value={form.ceo_profile_education_items || ""} onChange={set("ceo_profile_education_items")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use one education item per line in the format: Title|Subtitle|School|Extra|Body</CmsHint>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Awards Title</Label>
                <Input value={form.ceo_profile_awards_title || ""} onChange={set("ceo_profile_awards_title")} className="mt-1.5" />
              </div>
              <div>
                <Label>Philosophy Note</Label>
                <Input value={form.ceo_profile_philosophy_note || ""} onChange={set("ceo_profile_philosophy_note")} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label>Awards Intro</Label>
              <Textarea rows={4} value={form.ceo_profile_awards_intro || ""} onChange={set("ceo_profile_awards_intro")} className="mt-1.5" />
            </div>
            <div>
              <Label>Awards Items</Label>
              <Textarea rows={8} value={form.ceo_profile_awards_items || ""} onChange={set("ceo_profile_awards_items")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use one award per line in the format: Date|Title|Issuer|Note</CmsHint>
            </div>
          </div>
        </SectionCard>

        <SectionCard
          icon={SettingsIcon}
          title="Homepage sync"
          description="Homepage category, featured products, and CTA copy."
        >
          <div className="grid gap-6">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Categories Badge</Label>
                <Input value={form.home_categories_badge || ""} onChange={set("home_categories_badge")} className="mt-1.5" />
              </div>
              <div>
                <Label>Featured Badge</Label>
                <Input value={form.home_featured_badge || ""} onChange={set("home_featured_badge")} className="mt-1.5" />
              </div>
              <div>
                <Label>Categories Title</Label>
                <Input value={form.home_categories_title || ""} onChange={set("home_categories_title")} className="mt-1.5" />
              </div>
              <div>
                <Label>Categories Highlight</Label>
                <Input value={form.home_categories_highlight || ""} onChange={set("home_categories_highlight")} className="mt-1.5" />
              </div>
              <div>
                <Label>Featured Title</Label>
                <Input value={form.home_featured_title || ""} onChange={set("home_featured_title")} className="mt-1.5" />
              </div>
              <div className="sm:col-span-2">
                <Label>Featured Subtitle</Label>
                <Textarea rows={3} value={form.home_featured_subtitle || ""} onChange={set("home_featured_subtitle")} className="mt-1.5" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>CTA Title</Label>
                <Input value={form.home_cta_title || ""} onChange={set("home_cta_title")} className="mt-1.5" />
              </div>
              <div>
                <Label>CTA Highlight</Label>
                <Input value={form.home_cta_highlight || ""} onChange={set("home_cta_highlight")} className="mt-1.5" />
              </div>
              <div className="sm:col-span-2">
                <Label>CTA Body</Label>
                <Textarea rows={3} value={form.home_cta_body || ""} onChange={set("home_cta_body")} className="mt-1.5" />
              </div>
              <div>
                <Label>CTA Primary Label</Label>
                <Input value={form.home_cta_primary_label || ""} onChange={set("home_cta_primary_label")} className="mt-1.5" />
              </div>
              <div>
                <Label>CTA Secondary Label</Label>
                <Input value={form.home_cta_secondary_label || ""} onChange={set("home_cta_secondary_label")} className="mt-1.5" />
              </div>
            </div>
          </div>
        </SectionCard>

        <SectionCard
          icon={SettingsIcon}
          title="Products page sync"
          description="Controls the public catalog heading, filtering hints, and empty states."
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Products Badge</Label>
              <Input value={form.products_badge || ""} onChange={set("products_badge")} className="mt-1.5" />
            </div>
            <div>
              <Label>Products Title</Label>
              <Input value={form.products_title || ""} onChange={set("products_title")} className="mt-1.5" />
            </div>
            <div>
              <Label>Products Highlight</Label>
              <Input value={form.products_highlight || ""} onChange={set("products_highlight")} className="mt-1.5" />
            </div>
            <div>
              <Label>Search Placeholder</Label>
              <Input value={form.products_search_placeholder || ""} onChange={set("products_search_placeholder")} className="mt-1.5" />
            </div>
            <div className="sm:col-span-2">
              <Label>Products Subtitle</Label>
              <Textarea rows={3} value={form.products_subtitle || ""} onChange={set("products_subtitle")} className="mt-1.5" />
            </div>
            <div className="sm:col-span-2">
              <Label>Empty State Text</Label>
              <Input value={form.products_empty_text || ""} onChange={set("products_empty_text")} className="mt-1.5" />
            </div>
          </div>
        </SectionCard>

        <SectionCard
          icon={ShieldCheck}
          title="Trust Center sync"
          description="Public verification messaging and due-diligence FAQ content."
        >
          <div className="grid gap-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Trust Hero Title</Label>
                <Input value={form.trust_hero_title || ""} onChange={set("trust_hero_title")} className="mt-1.5" />
              </div>
              <div>
                <Label>Trust Hero Highlight</Label>
                <Input value={form.trust_hero_highlight || ""} onChange={set("trust_hero_highlight")} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label>Trust Hero Subtitle</Label>
              <Textarea rows={3} value={form.trust_hero_subtitle || ""} onChange={set("trust_hero_subtitle")} className="mt-1.5" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Verified Since</Label>
                <Input value={form.trust_verified_since || ""} onChange={set("trust_verified_since")} className="mt-1.5" />
              </div>
              <div>
                <Label>Trust Card Note</Label>
                <Input value={form.trust_verified_note || ""} onChange={set("trust_verified_note")} className="mt-1.5" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Trust FAQ Badge</Label>
                <Input value={form.trust_faq_badge || ""} onChange={set("trust_faq_badge")} className="mt-1.5" />
              </div>
              <div>
                <Label>Trust FAQ Title</Label>
                <Input value={form.trust_faq_title || ""} onChange={set("trust_faq_title")} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label>Trust FAQ Items</Label>
              <Textarea
                rows={6}
                value={form.trust_faq_items || ""}
                onChange={set("trust_faq_items")}
                className="mt-1.5 font-mono-plex"
              />
              <CmsHint>Use one FAQ per line in the format: Question|Answer</CmsHint>
            </div>
          </div>
        </SectionCard>

        <SectionCard
          icon={SettingsIcon}
          title="Contact & footer sync"
          description="Public contact page, inquiry success state, and footer copy."
        >
          <div className="grid gap-6">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Contact Badge</Label>
                <Input value={form.contact_badge || ""} onChange={set("contact_badge")} className="mt-1.5" />
              </div>
              <div>
                <Label>Contact Title</Label>
                <Input value={form.contact_title || ""} onChange={set("contact_title")} className="mt-1.5" />
              </div>
              <div>
                <Label>Contact Highlight</Label>
                <Input value={form.contact_highlight || ""} onChange={set("contact_highlight")} className="mt-1.5" />
              </div>
              <div>
                <Label>Contact Form Title</Label>
                <Input value={form.contact_form_title || ""} onChange={set("contact_form_title")} className="mt-1.5" />
              </div>
              <div className="sm:col-span-2">
                <Label>Contact Subtitle</Label>
                <Textarea rows={3} value={form.contact_subtitle || ""} onChange={set("contact_subtitle")} className="mt-1.5" />
              </div>
              <div>
                <Label>Contact Success Title</Label>
                <Input value={form.contact_success_title || ""} onChange={set("contact_success_title")} className="mt-1.5" />
              </div>
              <div>
                <Label>Contact Success Message</Label>
                <Input value={form.contact_success_message || ""} onChange={set("contact_success_message")} className="mt-1.5" />
              </div>
            </div>
            <div className="grid gap-4">
              <div>
                <Label>Footer Description</Label>
                <Textarea rows={3} value={form.footer_description || ""} onChange={set("footer_description")} className="mt-1.5" />
              </div>
              <div>
                <Label>Footer Bottom Note</Label>
                <Input value={form.footer_bottom_note || ""} onChange={set("footer_bottom_note")} className="mt-1.5" />
              </div>
            </div>
          </div>
        </SectionCard>

        <SectionCard
          icon={Globe2}
          title="Social media"
          description="Footer icon links and contact shortcuts for public visitors."
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Facebook URL</Label>
              <Input value={form.facebook_url || ""} onChange={set("facebook_url")} className="mt-1.5 font-mono-plex" data-testid="settings-facebook" />
            </div>
            <div>
              <Label>Instagram URL</Label>
              <Input value={form.instagram_url || ""} onChange={set("instagram_url")} className="mt-1.5 font-mono-plex" data-testid="settings-instagram" />
            </div>
            <div>
              <Label>LinkedIn URL</Label>
              <Input value={form.linkedin_url || ""} onChange={set("linkedin_url")} className="mt-1.5 font-mono-plex" />
            </div>
            <div>
              <Label>X / Twitter URL</Label>
              <Input value={form.twitter_url || ""} onChange={set("twitter_url")} className="mt-1.5 font-mono-plex" />
            </div>
            <div>
              <Label>WhatsApp URL</Label>
              <Input value={form.whatsapp_url || ""} onChange={set("whatsapp_url")} className="mt-1.5 font-mono-plex" />
            </div>
          </div>
        </SectionCard>

        <SectionCard
          icon={Globe2}
          title="Navigation & label CMS"
          description="Control the remaining public navigation labels, footer headings, trust labels, and profile section labels."
        >
          <div className="grid gap-4">
            <div>
              <Label>Navbar Labels</Label>
              <Textarea rows={8} value={form.navbar_labels || ""} onChange={set("navbar_labels")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use one item per line in the format: key|value. Keys: home, products, company, trust, track, cta, admin</CmsHint>
            </div>
            <div>
              <Label>Footer Labels</Label>
              <Textarea rows={12} value={form.footer_labels || ""} onChange={set("footer_labels")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use key|value lines. Keys: verified, explore, contact, products, about, trust, track, contact_link, faq, terms, privacy, admin</CmsHint>
            </div>
            <div>
              <Label>Trust Page Labels</Label>
              <Textarea rows={10} value={form.trust_page_labels || ""} onChange={set("trust_page_labels")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use key|value lines. Keys: hero_badge, verified_label, library_badge, library_title, compliance_prefix, compliance_link, preview_badge, preview_only, dialog_view_only</CmsHint>
            </div>
            <div>
              <Label>About Card Labels</Label>
              <Textarea rows={8} value={form.about_page_card_labels || ""} onChange={set("about_page_card_labels")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use key|value lines. Keys: founder_access, founder_status, executive_access, executive_status, first_office, company_base, id_label</CmsHint>
            </div>
            <div>
              <Label>Shared Profile Labels</Label>
              <Textarea rows={4} value={form.profile_common_labels || ""} onChange={set("profile_common_labels")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use key|value lines. Keys: back</CmsHint>
            </div>
            <div>
              <Label>CEO Profile Section Labels</Label>
              <Textarea rows={8} value={form.ceo_profile_section_labels || ""} onChange={set("ceo_profile_section_labels")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use key|value lines. Keys: vision, leadership, experience, philosophy, languages, education, awards</CmsHint>
            </div>
            <div>
              <Label>Founder Profile Section Labels</Label>
              <Textarea rows={6} value={form.founder_profile_section_labels || ""} onChange={set("founder_profile_section_labels")} className="mt-1.5 font-mono-plex" />
              <CmsHint>Use key|value lines. Keys: story, legacy, timeline, facts, focus</CmsHint>
            </div>
          </div>
        </SectionCard>

        <SectionCard
          icon={HelpCircle}
          title="FAQ page CMS"
          description="Edit the entire public FAQ page from the admin panel."
        >
          <div className="grid gap-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>FAQ Badge</Label>
                <Input value={form.faq_page_badge || ""} onChange={set("faq_page_badge")} className="mt-1.5" />
              </div>
              <div>
                <Label>FAQ Title</Label>
                <Input value={form.faq_page_title || ""} onChange={set("faq_page_title")} className="mt-1.5" />
              </div>
              <div className="sm:col-span-2">
                <Label>FAQ Highlight</Label>
                <Input value={form.faq_page_highlight || ""} onChange={set("faq_page_highlight")} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label>FAQ Intro</Label>
              <Textarea rows={3} value={form.faq_page_intro || ""} onChange={set("faq_page_intro")} className="mt-1.5" />
            </div>
            <div>
              <Label>FAQ Items</Label>
              <Textarea
                rows={12}
                value={form.faq_page_items || ""}
                onChange={set("faq_page_items")}
                className="mt-1.5 font-mono-plex"
              />
              <CmsHint>Use one question per line in the format: Question|Answer</CmsHint>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>CTA Title</Label>
                <Input value={form.faq_page_cta_title || ""} onChange={set("faq_page_cta_title")} className="mt-1.5" />
              </div>
              <div>
                <Label>CTA Body</Label>
                <Textarea rows={3} value={form.faq_page_cta_body || ""} onChange={set("faq_page_cta_body")} className="mt-1.5" />
              </div>
              <div>
                <Label>CTA Primary Label</Label>
                <Input value={form.faq_page_cta_primary || ""} onChange={set("faq_page_cta_primary")} className="mt-1.5" />
              </div>
              <div>
                <Label>CTA Secondary Label</Label>
                <Input value={form.faq_page_cta_secondary || ""} onChange={set("faq_page_cta_secondary")} className="mt-1.5" />
              </div>
            </div>
          </div>
        </SectionCard>

        <SectionCard
          icon={FileText}
          title="Terms & Conditions CMS"
          description="Manage the public legal terms page directly from admin."
        >
          <div className="grid gap-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Terms Badge</Label>
                <Input value={form.terms_page_badge || ""} onChange={set("terms_page_badge")} className="mt-1.5" />
              </div>
              <div>
                <Label>Terms Title</Label>
                <Input value={form.terms_page_title || ""} onChange={set("terms_page_title")} className="mt-1.5" />
              </div>
              <div className="sm:col-span-2">
                <Label>Terms Highlight</Label>
                <Input value={form.terms_page_highlight || ""} onChange={set("terms_page_highlight")} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label>Terms Intro</Label>
              <Textarea rows={3} value={form.terms_page_intro || ""} onChange={set("terms_page_intro")} className="mt-1.5" />
            </div>
            <div>
              <Label>Terms Sections</Label>
              <Textarea
                rows={14}
                value={form.terms_page_sections || ""}
                onChange={set("terms_page_sections")}
                className="mt-1.5 font-mono-plex"
              />
              <CmsHint>Use one section per line in the format: Title|Body</CmsHint>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>CTA Title</Label>
                <Input value={form.terms_page_cta_title || ""} onChange={set("terms_page_cta_title")} className="mt-1.5" />
              </div>
              <div>
                <Label>CTA Body</Label>
                <Textarea rows={3} value={form.terms_page_cta_body || ""} onChange={set("terms_page_cta_body")} className="mt-1.5" />
              </div>
              <div>
                <Label>CTA Primary Label</Label>
                <Input value={form.terms_page_cta_primary || ""} onChange={set("terms_page_cta_primary")} className="mt-1.5" />
              </div>
              <div>
                <Label>CTA Secondary Label</Label>
                <Input value={form.terms_page_cta_secondary || ""} onChange={set("terms_page_cta_secondary")} className="mt-1.5" />
              </div>
            </div>
          </div>
        </SectionCard>

        <SectionCard
          icon={FileQuestion}
          title="Privacy Policy CMS"
          description="Manage the public privacy page directly from admin."
        >
          <div className="grid gap-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Privacy Badge</Label>
                <Input value={form.privacy_page_badge || ""} onChange={set("privacy_page_badge")} className="mt-1.5" />
              </div>
              <div>
                <Label>Privacy Title</Label>
                <Input value={form.privacy_page_title || ""} onChange={set("privacy_page_title")} className="mt-1.5" />
              </div>
              <div className="sm:col-span-2">
                <Label>Privacy Highlight</Label>
                <Input value={form.privacy_page_highlight || ""} onChange={set("privacy_page_highlight")} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label>Privacy Intro</Label>
              <Textarea rows={3} value={form.privacy_page_intro || ""} onChange={set("privacy_page_intro")} className="mt-1.5" />
            </div>
            <div>
              <Label>Privacy Sections</Label>
              <Textarea
                rows={14}
                value={form.privacy_page_sections || ""}
                onChange={set("privacy_page_sections")}
                className="mt-1.5 font-mono-plex"
              />
              <CmsHint>Use one section per line in the format: Title|Body</CmsHint>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>CTA Title</Label>
                <Input value={form.privacy_page_cta_title || ""} onChange={set("privacy_page_cta_title")} className="mt-1.5" />
              </div>
              <div>
                <Label>CTA Body</Label>
                <Textarea rows={3} value={form.privacy_page_cta_body || ""} onChange={set("privacy_page_cta_body")} className="mt-1.5" />
              </div>
              <div>
                <Label>CTA Primary Label</Label>
                <Input value={form.privacy_page_cta_primary || ""} onChange={set("privacy_page_cta_primary")} className="mt-1.5" />
              </div>
              <div>
                <Label>CTA Secondary Label</Label>
                <Input value={form.privacy_page_cta_secondary || ""} onChange={set("privacy_page_cta_secondary")} className="mt-1.5" />
              </div>
            </div>
          </div>
        </SectionCard>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saveMut.isPending}
            className="btn-primary disabled:opacity-60"
            data-testid="settings-save"
          >
            {saveMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save settings
          </button>
        </div>
      </form>

      <form onSubmit={changePassword} className="mt-8">
        <SectionCard
          icon={FileKey2}
          title="Admin security"
          description="Change the admin panel login password from inside the admin panel."
        >
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Current password</Label>
              <Input
                type="password"
                value={passwordForm.current_password}
                onChange={setPassword("current_password")}
                className="mt-1.5"
                autoComplete="current-password"
              />
            </div>
            <div>
              <Label>New password</Label>
              <Input
                type="password"
                value={passwordForm.new_password}
                onChange={setPassword("new_password")}
                className="mt-1.5"
                autoComplete="new-password"
              />
              <CmsHint>Use at least 6 characters.</CmsHint>
            </div>
            <div className="md:col-span-2">
              <Label>Confirm new password</Label>
              <Input
                type="password"
                value={passwordForm.confirm_password}
                onChange={setPassword("confirm_password")}
                className="mt-1.5"
                autoComplete="new-password"
              />
            </div>
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[var(--rsi-purple)]/10 bg-[var(--rsi-purple)]/5 p-4">
            <div className="flex items-start gap-3">
              <LockKeyhole className="h-5 w-5 mt-0.5 text-[var(--rsi-purple)]" />
              <p className="text-sm leading-6 text-gray-700">
                After you change the password here, the next admin login will use the new password in both backend mode and localhost mock mode.
              </p>
            </div>
            <button type="submit" disabled={passwordMut.isPending} className="btn-primary disabled:opacity-60">
              {passwordMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              Update password
            </button>
          </div>
        </SectionCard>
      </form>
    </div>
  );
}
